/**
 * Hooldusraamat export — the EVS 807 maintenance book.
 *
 * Produces a three-sheet workbook an auditor can read on its own:
 *   1. Object      — what this book covers (building, company, totals)
 *   2. Equipment   — the maintenance plan, one row per piece of equipment
 *   3. Journal     — every service entry, newest first, with act links
 *
 * Two entry points, one builder:
 *   - a building card, scoped to that building and grouped by zone
 *   - the equipment tab, scoped to whatever the current filter shows
 *     (so it works for tenants that never set up buildings at all)
 */

import { t } from '../i18n.js'
import { formatDate, maintenanceStatus } from '../utils.js'
import { buildingsCache, buildingNameFor, zoneNameFor } from './buildings.js'
import { fetchServiceHistory, specialistName, workItemsText, totalHours } from './service-history.js'

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } }

/** Localised maintenance status for one piece of equipment. */
function maintenanceStatusText(equipment) {
  const { status, days } = maintenanceStatus(equipment.next_maintenance_date)
  return {
    overdue: () => t('maintenance.overdueByDays').replace('{days}', Math.abs(days)),
    soon:    () => t('maintenance.dueInDays').replace('{days}', days),
    ok:      () => t('maintenance.onSchedule'),
    none:    () => t('maintenance.noPlan'),
  }[status]()
}

/** Apply the shared header/filter/freeze treatment to one worksheet. */
function styleSheet(worksheet) {
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = HEADER_FILL
  worksheet.views = [{ state: 'frozen', ySplit: 1 }]
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: worksheet.columns.length },
  }
}

function buildObjectSheet(workbook, { building, equipment, tenantName }) {
  const sheet = workbook.addWorksheet(t('hooldusraamat.sheetObject'))
  sheet.columns = [
    { header: t('hooldusraamat.field'), key: 'field', width: 30 },
    { header: t('hooldusraamat.value'), key: 'value', width: 50 },
  ]

  const overdue = equipment.filter(
    e => maintenanceStatus(e.next_maintenance_date).status === 'overdue'
  ).length
  const scheduled = equipment.filter(e => e.next_maintenance_date).length

  const rows = [
    [t('hooldusraamat.company'), tenantName || '-'],
    [t('buildings.navTitle'), building?.name || t('hooldusraamat.allEquipment')],
    [t('buildings.address'), building?.address || '-'],
    [t('buildings.notes'), building?.notes || '-'],
    [t('hooldusraamat.equipmentCount'), equipment.length],
    [t('hooldusraamat.scheduledCount'), scheduled],
    [t('maintenance.overdueLabel'), overdue],
    [t('hooldusraamat.generatedAt'), new Date().toLocaleString()],
  ]

  rows.forEach(([field, value]) => sheet.addRow({ field, value }))
  sheet.getColumn('field').font = { bold: true }
  sheet.getRow(1).font = { bold: true }
  sheet.getRow(1).fill = HEADER_FILL
  return sheet
}

function buildEquipmentSheet(workbook, equipment) {
  const sheet = workbook.addWorksheet(t('hooldusraamat.sheetEquipment'))
  sheet.columns = [
    { header: t('buildings.navTitle'), key: 'building', width: 22 },
    { header: t('zones.title'), key: 'zone', width: 22 },
    { header: t('equipment.name'), key: 'name', width: 28 },
    { header: t('hooldusraamat.type'), key: 'type', width: 18 },
    { header: t('equipment.serialLabel'), key: 'serial', width: 18 },
    { header: t('equipment.nfcTagLabel'), key: 'nfc', width: 18 },
    { header: t('equipment.statusLabel'), key: 'status', width: 12 },
    { header: t('maintenance.intervalLabel'), key: 'interval', width: 22 },
    { header: t('equipment.maintenanceDate'), key: 'last', width: 16 },
    { header: t('equipment.nextMaintenanceDate'), key: 'next', width: 16 },
    { header: t('common.status'), key: 'maintStatus', width: 22 },
    { header: t('equipment.checklistLabel'), key: 'checklist', width: 24 },
  ]

  equipment.forEach(item => {
    sheet.addRow({
      building: buildingNameFor(item),
      zone: zoneNameFor(item),
      name: item.name || '-',
      type: item.equipment_type?.name || '-',
      serial: item.serial_number || '-',
      nfc: item.nfc_id || '-',
      status: item.status || '-',
      interval: item.maintenance_interval_days ?? '-',
      last: item.last_maintenance_date ? formatDate(item.last_maintenance_date) : '-',
      next: item.next_maintenance_date ? formatDate(item.next_maintenance_date) : '-',
      maintStatus: maintenanceStatusText(item),
      checklist: item.checklist?.name || '-',
    })
  })

  styleSheet(sheet)
  return sheet
}

function buildJournalSheet(workbook, entries, equipmentById) {
  const sheet = workbook.addWorksheet(t('hooldusraamat.sheetJournal'))
  sheet.columns = [
    { header: t('serviceHistory.date'), key: 'date', width: 14 },
    { header: t('zones.title'), key: 'zone', width: 22 },
    { header: t('equipment.title'), key: 'equipment', width: 28 },
    { header: t('serviceHistory.mechanic'), key: 'mechanic', width: 22 },
    { header: t('hooldusraamat.workPerformed'), key: 'work', width: 55 },
    { header: t('hooldusraamat.hours'), key: 'hours', width: 10 },
    { header: t('equipment.checklistLabel'), key: 'checklist', width: 14 },
    { header: t('equipment.engine_hours'), key: 'engineHours', width: 14 },
    { header: t('serviceHistory.act'), key: 'act', width: 40 },
  ]

  entries.forEach(entry => {
    const item = equipmentById[entry.equipment_id]
    const hours = totalHours(entry)

    const row = sheet.addRow({
      date: formatDate(entry.work_date),
      zone: item ? zoneNameFor(item) : '-',
      equipment: item?.name || '-',
      mechanic: specialistName(entry),
      work: workItemsText(entry) || '-',
      hours: hours > 0 ? hours : '-',
      checklist: entry.completed_checklist_items ?? '-',
      engineHours: entry.engine_hours ?? '-',
      act: entry.act_pdf_url || '-',
    })

    // Make the act a real clickable link rather than a bare URL string
    if (entry.act_pdf_url) {
      const cell = row.getCell('act')
      cell.value = { text: t('serviceHistory.act'), hyperlink: entry.act_pdf_url }
      cell.font = { color: { argb: 'FF2563EB' }, underline: true }
    }
  })

  styleSheet(sheet)
  return sheet
}

function downloadWorkbookAs(workbook, filename) {
  return workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  })
}

/** Sort equipment so the book reads building -> zone -> name. */
function sortForBook(equipment) {
  return [...equipment].sort((a, b) =>
    buildingNameFor(a).localeCompare(buildingNameFor(b))
    || zoneNameFor(a).localeCompare(zoneNameFor(b))
    || (a.name || '').localeCompare(b.name || '')
  )
}

function safeFilenamePart(text) {
  return (text || '').replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '')
}

/**
 * Build and download the maintenance book.
 *
 * @param {object}   options
 * @param {Array}    options.equipment  equipment to cover (already scoped)
 * @param {object?}  options.building   the building, when scoped to one
 * @param {string?}  options.tenantName company name for the cover sheet
 */
export async function exportHooldusraamat({ equipment, building = null, tenantName = '' }) {
  if (typeof ExcelJS === 'undefined') {
    throw new Error('ExcelJS is not loaded')
  }
  if (!equipment || equipment.length === 0) {
    alert(t('equipment.exportNoData'))
    return
  }

  const ordered = sortForBook(equipment)
  const equipmentById = Object.fromEntries(ordered.map(e => [e.id, e]))
  const entries = await fetchServiceHistory(ordered.map(e => e.id))

  const workbook = new ExcelJS.Workbook()
  buildObjectSheet(workbook, { building, equipment: ordered, tenantName })
  buildEquipmentSheet(workbook, ordered)
  buildJournalSheet(workbook, entries, equipmentById)

  const date = new Date().toISOString().substring(0, 10)
  const scope = safeFilenamePart(building?.name) || 'all'
  await downloadWorkbookAs(workbook, `hooldusraamat_${scope}_${date}.xlsx`)
}

// ==================== ENTRY POINTS ====================

export function initHooldusraamat() {
  /** From a building card — everything inside that building. */
  window.exportBuildingBook = async function (buildingId, btn) {
    const building = buildingsCache.find(b => b.id === buildingId) || null
    const { supabase, currentUser } = await import('./main.js')

    const original = btn?.innerHTML
    if (btn) {
      btn.disabled = true
      btn.textContent = t('equipment.exporting')
    }

    try {
      const { data, error } = await supabase
        .from('equipment')
        .select('*, equipment_type:equipment_types (name, code), checklist:checklists!equipment_checklist_id_fkey (id, name)')
        .eq('tenant_id', currentUser.tenant_id)
        .eq('building_id', buildingId)
        .is('deleted_at', null)

      if (error) throw error

      await exportHooldusraamat({
        equipment: data || [],
        building,
        tenantName: currentUser.tenant?.company_name,
      })
    } catch (error) {
      console.error('Hooldusraamat export failed:', error)
      alert(error.message || t('common.error'))
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = original
      }
    }
  }

  /** From the equipment tab — whatever the current filter shows. */
  document.getElementById('exportBookBtn')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget
    const original = btn.innerHTML
    btn.disabled = true
    btn.textContent = t('equipment.exporting')

    try {
      const { getFilteredEquipment, currentUser } = await import('./main.js')
      await exportHooldusraamat({
        equipment: getFilteredEquipment(),
        tenantName: currentUser.tenant?.company_name,
      })
    } catch (error) {
      console.error('Hooldusraamat export failed:', error)
      alert(error.message || t('common.error'))
    } finally {
      btn.disabled = false
      btn.innerHTML = original
    }
  })
}
